import { useState, useRef, useEffect, useMemo } from "react";
import AiSummaryIcon from "../../assets/svg/ai-summary-icon";
//import VerbafloLogoFullIcon from "../../assets/svg/verbaflo-logo-full-icon";
import Button from "../Button/Button";
import ChevronDownIcon from "../../assets/svg/v1/chevron-down";
import TextMessageBlock from "./TextMessageBlock";
import VerbafloLogoIcon from "../../assets/images/verbaflo-logo.png";
import { Lead } from "../../pages/Communications/utils/types";
import { useSearchHighlight } from "../../context/SearchHighlightContext";

interface Message {
  content: string;
  role: string;
  timestamp: string;
  kind: string;
  transcript_text?: string;
  message_id?: string;
  agent_name?: string;
  translated_content?: string | null;
}

interface AICallTranscriptProps {
  title?: string;
  showLogo?: boolean;
  className?: string;
  transcript?: string; // Keep for backward compatibility
  messages?: Message[]; // New prop for message objects
  isSent?: boolean;
  showAllTranslation?: boolean;
  currentLead?: Lead;
  agentName?: string;
  showOriginalContent?: boolean;
}

const AICallTranscript = ({
  title = "AI Summary",
  showLogo = true,
  className = "",
  transcript,
  messages,
  isSent = false,
  showAllTranslation = false,
  currentLead,
  showOriginalContent,
}: AICallTranscriptProps) => {
  const [isTranscriptVisible, setIsTranscriptVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const previousMatchIdRef = useRef<string>("");
  const { currentMatchIndex, matchedMessageIds } = useSearchHighlight();

  const handleToggleTranscript = () => {
    setIsTranscriptVisible((prev) => !prev);

    if (!isTranscriptVisible && containerRef.current) {
      setTimeout(() => {
        containerRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 200);
    }
  };

  // Handle both message objects and string transcript for backward compatibility
  const transcriptMessages = useMemo(() => {
    return messages
      ? messages.filter((msg) => {
          const content = msg?.content || msg?.transcript_text;
          return (
            content &&
            !content.toLowerCase().includes("call initiated") &&
            !content.toLowerCase().includes("campaign call")
          );
        })
      : transcript
          ?.split("\n")
          .filter((msg) => msg?.trim() !== "")
          .map((msg, index) => ({
            content: msg
              ?.replace(/^(Agent:|Customer:|agent:|user:)/i, "")
              ?.trim(),
            role: msg?.toLowerCase()?.includes("agent:") ? "agent" : "user",
            timestamp: "",
            kind: "chat",
            message_id: `transcript-${index}`,
            transcript_text: undefined,
            agent_name: undefined,
            translated_content: null,
          })) || [];
  }, [messages, transcript]);

  // Get the set of message IDs in this transcript for quick lookup
  const transcriptMessageIds = useMemo(() => {
    return new Set(
      transcriptMessages.map((msg) => msg.message_id).filter(Boolean),
    );
  }, [transcriptMessages]);

  useEffect(() => {
    const currentMatchId = matchedMessageIds[currentMatchIndex] || "";
    const matchIdChanged = currentMatchId !== previousMatchIdRef.current;

    previousMatchIdRef.current = currentMatchId;

    if (
      matchIdChanged &&
      currentMatchId !== "" &&
      transcriptMessageIds.has(currentMatchId)
    ) {
      setIsTranscriptVisible(true);
    }
  }, [currentMatchIndex, matchedMessageIds, transcriptMessageIds]);

  useEffect(() => {
    previousMatchIdRef.current = "";
  }, [messages]);

  return (
    <div
      ref={containerRef}
      className={`bg-gradient-ai-card flex flex-col gap-[10px] px-[12px] py-[10px] rounded-[8px] w-[500px]  ${
        isSent ? "ml-auto" : "mr-auto"
      } ${className}`}
      data-message-id={`${messages?.[0]?.message_id}-container`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-[4px]">
          <AiSummaryIcon className="w-[18px] h-[18px] text-verba-blue dark:text-verba-blue" />
          <p className="text-heading-text dark:text-heading-text-dk font-medium text-md">
            {title}
          </p>
        </div>

        {showLogo && (
          // <VerbafloLogoFullIcon className="w-[70px] h-[20px] text-verba-indigo dark:text-verba-indigo-dk" />
          <div className="flex items-center gap-[2px]">
            <img
              src={VerbafloLogoIcon}
              alt="Verbaflo Logo"
              className="w-[18px] h-[18px]"
            />
            <p className="text-verba-indigo dark:text-verba-indigo-dk text-xs font-bold">
              VerbaFlo.
            </p>
          </div>
        )}
      </div>

      <Button
        innerText={
          isTranscriptVisible ? "Hide Call Transcript" : "View Call Transcript"
        }
        Icon={ChevronDownIcon}
        buttonContainerClass="max-w-fit pl-0 pr-0 pt-0 pb-0"
        innerTextClass="text-md font-normal text-verba-blue-dark-mode dark:text-verba-blue-dark-mode-dk hover:underline"
        iconClass={`w-[10px] h-[10px] text-verba-blue-dark-mode dark:text-verba-blue-dark-mode-dk stroke-2 transition-transform duration-400 ease-in-out ${
          isTranscriptVisible ? "rotate-180" : ""
        }`}
        onClick={handleToggleTranscript}
      />
      <div
        className={`grid transition-all duration-400 ease-in-out  ${
          isTranscriptVisible
            ? "h-auto grid-rows-[1fr] opacity-100"
            : "h-0 grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <div className="flex flex-col gap-[10px]">
            {transcriptMessages?.map((message, index) => {
              const isAgent = message?.role === "agent";
              const isAssistant = message?.role === "assistant";
              const messageContent =
                message?.content || message?.transcript_text || "";
              const messageId = message?.message_id || `transcript-${index}`;

              return (
                <TextMessageBlock
                  key={messageId}
                  message={messageContent}
                  modality="call"
                  messageId={messageId}
                  isSent={isAgent || isAssistant}
                  translatedMessage={message?.translated_content || undefined}
                  showTranslate={!!message?.translated_content}
                  showAllTranslation={!showOriginalContent}
                  showModality={false}
                  showAiResponseTime={false}
                  showHoverEffect={true}
                  showTimestamp={true}
                  timestamp={message?.timestamp || ""}
                  showDotAfterTimestamp={true}
                  iconType={
                    isAgent ? "agent" : isAssistant ? "verbaflo" : "participant"
                  }
                  showFlagDot={false}
                  participantInfo={{
                    text: isAgent
                      ? message?.agent_name || "Agent"
                      : isAssistant
                        ? "VerbaFlo"
                        : currentLead?.name || "",
                    fullName: isAgent
                      ? message?.agent_name
                      : isAssistant
                        ? "verbaFlo"
                        : currentLead?.name || "Anonymous",
                    size: 16,
                    showOnlineStatus: currentLead?.isOnline || false,
                    isOnline: currentLead?.isOnline || true,
                  }}
                  regularMessage={{
                    id: messageId,
                    message_id: messageId,
                    content: messageContent,
                    transcript_text: message?.transcript_text || null,
                    role:
                      (message?.role as "assistant" | "user" | "agent") ||
                      "user",
                    kind: message?.kind || "transcript",
                    timestamp: message?.timestamp || "",
                    language: "",
                    template_meta_data: {},
                    channel: "call",
                  }}
                  leadDetails={null}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AICallTranscript;
